import { generateTabData } from './TabDataTemplate';
import {
    seoUrlSport,
    seoUrlCouponSport,
    seoUrlCompetitionSport,
    seoUrlTournamentOutrights,
    seoUrlTournament,
} from '~spa/Utils/SeoUrls';
import { getSportIdBySlug } from '~shared/utils/sportsbook';
import i18n from '~shared/utils/i18n';
import { FOOTBALL_SLUG } from '~shared/utils/sportsbook';
import {
    ssGetState,
} from '~SpectateStore';
import {
    selectIsCompetitionOnlySport,
    selectIsMultiGroupedOnlySport,
} from '~SpectateStore/selectors/clientStateSelectors';

/**
 * Generates default tabs data for a given set of slugs
 * @param  {String} systemSportSlug                    Sport slug (non localized)
 * @param  {String} sportSlug                    Sport slug
 * @param  {String} categorySlug (optional)      Category slug
 * @param  {String} tournamentSlug (optional)    Tournament Slug
 * @param  {Number} tournamentId (optional)    Tournament Id
 * @return {Array}                               List of tabs
 */
export const getSportDefaultTabs = (
    systemSportSlug,
    sportSlug,
    categorySlug = null,
    tournamentSlug = null,
    tournamentId = null,
) => {
    const segments = [
            (!tournamentSlug) ? sportSlug : systemSportSlug,
            categorySlug,
            tournamentSlug,
        ]
        .filter((slug) => Boolean(slug))
        .join('/');

    const baseSegments = `/${segments}`;
    const sportId = getSportIdBySlug(systemSportSlug);

    const firstTab = (!tournamentSlug)
        ? generateTabData('Matches', seoUrlSport(baseSegments, sportId))
        : generateTabData('Matches', seoUrlTournament(`${baseSegments}/`, tournamentId));
    const secondTab = (!tournamentSlug)
        ? generateTabData('Competitions', seoUrlCompetitionSport(`${baseSegments}/${i18n.t('url.competitions')}`, sportId))
        : generateTabData('Outrights', seoUrlTournamentOutrights(`${baseSegments}/${i18n.t('url.outrights')}`, tournamentId));

    return [
        firstTab,
        secondTab,
    ];
};

/**
 * Generates the tabs data for Football Homepage
 * @param {String} localizedSportSlug The localized sport slug
 * @return {Array}   List of tabs
 */
export const getFootballHomepageTabs = (localizedSportSlug) => {
    const sportId = getSportIdBySlug('football');

    return [
        generateTabData('Featured', seoUrlSport(`/${localizedSportSlug}`, sportId)),
        generateTabData('Coupons', seoUrlCouponSport(`/${localizedSportSlug}/${i18n.t('url.coupons')}`, sportId)),
        generateTabData('Competitions', seoUrlCompetitionSport(`/${localizedSportSlug}/${i18n.t('url.competitions')}`, sportId)),
    ];
};

/**
 * Generates default tabs data for a given set of slugs
 * @param  {String} systemSportSlug                    Sport slug (non localized)
 * @param  {String} sportSlug                    Sport slug
 * @param  {String} categorySlug (optional)      Category slug
 * @param  {String} tournamentSlug (optional)    Tournament Slug
 * @param  {Number} tournamentId (optional)    Tournament Id
 * @param  {Boolean} isOutrights (optional)    Is for an outrights page
 * @return {Array}                               List of tabs
 */
export const getTabs = (
    systemSportSlug,
    sportSlug,
    {
        categorySlug = null,
        tournamentSlug = null,
        tournamentId = null,
        isCompetitions = false,
    } = {}
) => {
    if (systemSportSlug === FOOTBALL_SLUG && isCompetitions) {
        return getFootballHomepageTabs(FOOTBALL_SLUG);
    }

    const isCompetitionsOnlySport = selectIsCompetitionOnlySport(ssGetState(), systemSportSlug);
    const isMultiGroupedOnlySport = selectIsMultiGroupedOnlySport(ssGetState(), systemSportSlug);

    if (isCompetitionsOnlySport || isMultiGroupedOnlySport) {
        return [];
    }

    return getSportDefaultTabs(systemSportSlug, sportSlug, categorySlug, tournamentSlug, tournamentId);
};
